import { useEffect, useState } from "react";
import { KeyRound, Loader2, CheckCircle2, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { clientApi } from "../lib/api";
import { toast } from "sonner";

/**
 * Client-triggered password recovery.
 *
 * The client types their NIT and we ask the backend to email them a
 * time-limited reset link (30 min). We intentionally never reveal whether
 * the NIT is registered — the same success screen is shown either way.
 */
export const ForgotPasswordModal = ({ open, onOpenChange, initialNit = "" }) => {
  const [nit, setNit] = useState(initialNit);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [hint, setHint] = useState("");

  // When the modal opens, sync the NIT from the login form so the user
  // doesn't have to retype it.
  useEffect(() => {
    if (open) setNit(initialNit || "");
  }, [open, initialNit]);

  const reset = () => { setNit(initialNit); setSent(false); setHint(""); setSubmitting(false); };

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = (nit || "").trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const { data } = await clientApi.post("/portal/password-reset/request", { nit: trimmed });
      setSent(true);
      setHint(data?.email_hint || "");
    } catch (err) {
      const msg = err?.response?.data?.detail || "No pudimos procesar la solicitud. Intenta de nuevo.";
      toast.error(typeof msg === "string" ? msg : "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="forgot-password-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-cm-red" />
            Recuperar contraseña
          </DialogTitle>
          <DialogDescription className="text-cm-ink-soft">
            {sent
              ? "Revisa tu bandeja de entrada."
              : "Te enviaremos un enlace al correo registrado con tu NIT en Arcos para que puedas crear una nueva contraseña."}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="space-y-3 mt-2" data-testid="forgot-sent">
            <div className="bg-green-50 border border-green-200 text-green-900 rounded-md p-4 text-sm flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-700 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Solicitud recibida</p>
                <p className="text-xs mt-1 text-green-800/90 leading-relaxed">
                  Si el NIT está registrado y tiene un correo asociado, te enviaremos un enlace de recuperación.
                  {hint && <> Buzón sugerido: <span className="font-mono">{hint}</span>.</>}
                </p>
                <p className="text-xs mt-2 text-green-800/70">
                  El enlace vence en 30 minutos. Revisa también spam/correo no deseado.
                </p>
              </div>
            </div>
            <p className="text-xs text-cm-ink-soft">
              ¿No llegó el correo o tu email en Arcos está desactualizado?
              {" "}
              <a
                href="https://wa.me/573215006806?text=Hola%2C%20mi%20correo%20no%20est%C3%A1%20actualizado%20en%20Arcos%20y%20no%20puedo%20recuperar%20la%20contrase%C3%B1a%20del%20portal."
                target="_blank" rel="noopener noreferrer"
                className="text-cm-red hover:underline font-medium"
                data-testid="forgot-whatsapp-fallback"
              >
                Escríbenos por WhatsApp
              </a>
            </p>
            <div className="flex justify-end">
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="forgot-close-btn">
                Cerrar
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4 mt-2">
            <div>
              <label className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium block mb-1.5">
                NIT del cliente
              </label>
              <Input
                value={nit}
                onChange={(e) => setNit(e.target.value.replace(/\D/g, ""))}
                placeholder="Ej: 900123456"
                autoFocus
                required
                inputMode="numeric"
                data-testid="forgot-nit-input"
                className="border-cm-border h-11"
              />
            </div>
            <div className="bg-cm-cream-alt/60 border border-cm-border rounded p-3 text-xs text-cm-ink-soft flex gap-2">
              <Mail className="h-4 w-4 flex-shrink-0 text-cm-ink-soft mt-0.5" />
              <p>
                Enviaremos el enlace al correo que CM tiene registrado en Arcos para tu NIT.
                Si no lo recibes en 5 minutos, verifica spam o contáctanos.
              </p>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                data-testid="forgot-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting || !nit.trim()}
                className="btn-cm-primary"
                data-testid="forgot-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enviando...</>
                ) : (
                  "Enviar enlace"
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
